import {useEffect, useRef, useState, FormEvent} from 'react'
import {Tooltip, Modal} from 'bootstrap'
import {useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-toastify'
import Select from 'react-select'
import { getBlockerUsers, getBlockedUsers, blockUser } from '../../../_requests'

const BlockUserButton = () => {
  
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [blockerUserError, setBlockerUserError] = useState('')
  const [blockedUserError, setBlockedUserError] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [userData, setUserData] = useState<any[]>([])
  const [blockedUsersData, setBlockedUsersData] = useState<any[]>([])
  const [selectedBlocker, setSelectedBlocker] = useState<any>(null)
  const [selectedBlocked, setSelectedBlocked] = useState<any>(null)

  const modalRef = useRef<HTMLDivElement | null>(null)
  const modalInstance = useRef<Modal | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    ) as HTMLElement[]

    const instances = tooltipTriggerList.map((el) => new Tooltip(el))

    return () => {
      instances.forEach((i) => i.dispose())
    }
  }, [])

  useEffect(() => {
    if (!modalRef.current) {
      return
    }

    modalInstance.current = new Modal(modalRef.current, {
      backdrop: 'static',
      keyboard: false,
    })
  }, [])

  useEffect(() => {
    if (selectedBlocker) {
      fetchBlockedUsers(selectedBlocker.value)
      setSelectedBlocked(null) // Clear blocked selection when blocker changes
    } else {
      setBlockedUsersData([])
      setSelectedBlocked(null)
    }
  }, [selectedBlocker])

  const openModal = () => {
    modalInstance.current?.show()
    fetchUsers()
  }

  const closeModal = () => {
    modalInstance.current?.hide()
    resetForm()
  }

  const resetForm = () => {
   
    setReason('')
    setBlockerUserError('')
    setBlockedUserError('')
    setReasonError('')
    setSelectedBlocker(null)
    setSelectedBlocked(null)
    setBlockedUsersData([])
  }

  const fetchUsers = async () => {
    try {
      const response = await getBlockerUsers()
      const users = response?.data || []
      setUserData(users)
    } catch (error: any) {
      toast.error('Failed to fetch users list.')
    }
  }

  const fetchBlockedUsers = async (blockerId: any) => {
    try {
      const response = await getBlockedUsers(blockerId)
      const users = response?.data || []
      setBlockedUsersData(users)
    } catch (error: any) {
      toast.error('Failed to fetch blocked users list.')
    }
  }

  const userOptions = userData
    .map((user: any) => ({
      value: parseInt(user.id),
      label: user.full_name,
    }))

  const blockedUserOptions = blockedUsersData
    .map((user: any) => ({
      value: parseInt(user.friend_id),
      label: user.full_name,
    }))

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    // Reset errors
    setBlockerUserError('')
    setBlockedUserError('')
    setReasonError('')

    // Validate fields
    const blockerError = !selectedBlocker ? 'Blocker User is required.' : ''
    const blockedError = !selectedBlocked ? 'Blocked User is required.' : ''
    const reasonErr = reason.trim() ? '' : 'Reason is required.'

    setBlockerUserError(blockerError)
    setBlockedUserError(blockedError)
    setReasonError(reasonErr)

    if (blockerError || blockedError || reasonErr) {
      return
    }

    setIsSubmitting(true)

    try {
      await blockUser({
        blocker_id: parseInt(selectedBlocker.value),
        blocked_id: parseInt(selectedBlocked.value),
        reason: reason.trim(),
        description: ''
      })

      toast.success('User blocked successfully.')
      queryClient.invalidateQueries({queryKey: ['block-user-list']})
      closeModal()
      resetForm()
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to block user.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <button
        type='button'
        className='btn btn-primary d-flex align-items-center justify-content-center'
        style={{width: '35px', height: '35px', borderRadius: '50%', padding: 0}}
        onClick={openModal}
        data-bs-toggle='tooltip'
        data-bs-placement='top'
        data-bs-container='body'
        title='Block User'
      >
        <i className='bi bi-plus fs-3 p-0'></i>
      </button>

      <div
        className='modal fade'
        id='kt_modal_block_user'
        tabIndex={-1}
        aria-labelledby='kt_modal_block_user_label'
        aria-hidden='true'
        ref={modalRef}
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='kt_modal_block_user_label'>
                Block User
              </h5>
              <button
                type='button'
                className='btn-close'
                onClick={closeModal}
                aria-label='Close'
              />
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className='modal-body'>
                <div className='mb-5'>
                  <label className='form-label'>Blocker User</label>
                  <Select
                    options={userOptions}
                    isSearchable
                    value={selectedBlocker}
                    onChange={(selected) => {
                      setSelectedBlocker(selected)
                      if (blockerUserError) setBlockerUserError('')
                    }}
                    placeholder='Search and select blocker user'
                    className={blockerUserError ? 'is-invalid' : ''}
                  />
                  {blockerUserError && <div className='text-danger mt-1'>{blockerUserError}</div>}
                </div>

                <div className='mb-5'>
                  <label className='form-label'>Blocked User</label>
                  <Select
                    options={blockedUserOptions}
                    isSearchable
                    value={selectedBlocked}
                    onChange={(selected) => {
                      setSelectedBlocked(selected)
                      if (blockedUserError) setBlockedUserError('')
                    }}
                    placeholder={selectedBlocker ? 'Search and select blocked user' : 'Select blocker user first'}
                    className={blockedUserError ? 'is-invalid' : ''}
                    isDisabled={!selectedBlocker}
                  />
                  {blockedUserError && <div className='text-danger mt-1'>{blockedUserError}</div>}
                </div>

                <div className='mb-5'>
                  <label className='form-label'>Reason</label>
                  <textarea
                    className='form-control'
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value)
                      if (reasonError) setReasonError('')
                    }}
                    placeholder='Enter reason for blocking'
                    rows={3}
                    required
                  />
                  {reasonError && <div className='text-danger mt-1'>{reasonError}</div>}
                </div>
              </div>

              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={closeModal}>
                  Cancel
                </button>
                <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                  {isSubmitting ? 'Blocking...' : 'Block'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default BlockUserButton
